import { useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import {
  Calculator, ClipboardList, Monitor, Building2, Map, HardHat,
  Building, DollarSign, Users, Palette, Laptop, Zap, Layers,
  Leaf, Megaphone, Scale, Compass, Wrench, BarChart3, Briefcase,
  BookOpen, Globe, ShieldCheck, Coffee, Lightbulb, ArrowRight, MapPin,
  Pencil, X, Award, MessageSquare, AlertTriangle } from 'lucide-react';
import { deptScheme, schemeForEmployee } from '../../utils/deptColors';
import { useIsDark } from '../../hooks/useIsDark';
import { getDeptSettings, saveDeptSettings } from '../../services/deptSettingsService';
import { StyledSelect, FIELD_CLASSES } from '../ui/StyledSelect';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { TenureChip } from '../TenureBadge/TenureBadge';
import { abbreviateTitle, sortEmployees } from '../../utils/employeeSort';
import { teamsChatUrl } from '../../utils/deepLinks';

function getDeptIcon(name) {
  const n = (name ?? '').toLowerCase();
  if (n.includes('account'))                                            return Calculator;
  if (n.includes('admin'))                                              return ClipboardList;
  if (n.includes('arch tech') || n.includes('architectural tech'))      return Monitor;
  if (n.includes('arch'))                                               return Compass;
  if (n.includes('construct'))                                          return HardHat;
  if (n.includes('corporate') || n.includes('executive'))               return Briefcase;
  if (n.includes('mep') || n.includes('mechanical') || n.includes('electrical') || n.includes('plumbing')) return Zap;
  if (n.includes('structural'))                                         return Layers;
  if (n.includes('engineer'))                                           return Wrench;
  if (n.includes('financ') || n.includes('budget'))                     return DollarSign;
  if (n.includes('human resource') || n.includes(' hr') || n === 'hr')  return Users;
  if (n.includes('interior'))                                           return Palette;
  if (n.includes('information tech') || n === 'it')                     return Laptop;
  if (n.includes('landscape'))                                          return Leaf;
  if (n.includes('legal') || n.includes('compliance'))                  return Scale;
  if (n.includes('market') || n.includes('communicat'))                 return Megaphone;
  if (n.includes('business dev'))                                       return BarChart3;
  if (n.includes('planning') || n.includes('urban'))                    return Map;
  if (n.includes('sustainab') || n.includes('green') || n.includes('leed')) return Globe;
  if (n.includes('practice'))                                           return Building2;
  if (n.includes('security') || n.includes('risk'))                     return ShieldCheck;
  if (n.includes('innovation') || n.includes('research'))               return Lightbulb;
  if (n.includes('facil') || n.includes('office'))                      return Coffee;
  if (n.includes('learn') || n.includes('train') || n.includes('talent')) return BookOpen;
  return Building;
}

function isOnline(employee) {
  const s = (employee.availability || '').toLowerCase();
  return s === 'available' || s === 'availableidle';
}

// The department's leader: the person managing the most colleagues inside
// the department, breaking ties toward whoever reports outside it (the
// branch root). Falls back to null for one-person departments.
function findDeptLead(deptEmployees) {
  if (deptEmployees.length < 2) return null;
  const ids = new Set(deptEmployees.map((e) => e.id));
  let best = null;
  let bestScore = 0;
  for (const employee of deptEmployees) {
    const reports = deptEmployees.filter((r) => r.managerId === employee.id).length;
    const isBranchRoot = !employee.managerId || !ids.has(employee.managerId);
    const score = reports * 2 + (isBranchRoot ? 1 : 0);
    if (reports > 0 && score > bestScore) {
      best = employee;
      bestScore = score;
    }
  }
  return best;
}

function initials(name) {
  return (name || '?').trim().split(/\s+/).map((p) => p[0]).join('').toUpperCase().slice(0, 2);
}

function AvatarStack({ employees, photos, isDark }) {
  // This stack only ever renders inside the department card's recessed footer
  // tray, so the ring that separates overlapping avatars has to be the TRAY
  // colour, not the card body's. It needs an opaque paint (a translucent one
  // would darken the avatar it overlaps), hence --card-tray-solid.
  const ringColor = 'var(--card-tray-solid)';
  const visible = employees.slice(0, 5);
  const overflow = employees.length - visible.length;

  if (employees.length === 0) return null;

  return (
    <div className="flex items-center">
      <div className="flex -space-x-2">
        {visible.map((emp) => {
          const src = photos?.[emp.id] || emp.openAssetPhoto;
          return src ? (
            <img
              key={emp.id}
              src={src}
              alt=""
              title={emp.displayName}
              className="w-7 h-7 rounded-full object-cover object-top flex-shrink-0"
              style={{ boxShadow: `0 0 0 2px ${ringColor}` }}
            />
          ) : (
            <span
              key={emp.id}
              title={emp.displayName}
              className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
              style={{ background: schemeForEmployee(emp).accent, boxShadow: `0 0 0 2px ${ringColor}` }}
            >
              {initials(emp.displayName)}
            </span>
          );
        })}
        {overflow > 0 && (
          <span
            className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0"
            style={{
              background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.05)',
              color: isDark ? '#a1a1aa' : '#64748b',
              boxShadow: `0 0 0 2px ${ringColor}`,
            }}
          >
            +{overflow}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Admin editor for a department: rename it (renaming to another existing
 * department's name merges them) and pin the "Led by" spotlight.
 */
function DeptEditModal({ dept, deptEmployees, currentLeadId, departmentCounts = {}, onClose, onSaved }) {
  const [name, setName] = useState(dept);
  const [leadId, setLeadId] = useState(currentLeadId || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pendingMerge, setPendingMerge] = useState(null);

  const persist = async (trimmed) => {
    setSaving(true);
    setError(null);
    const ok = await saveDeptSettings(dept, {
      rename: trimmed && trimmed !== dept ? trimmed : null,
      leadId: leadId || null,
    });
    setSaving(false);
    if (ok) onSaved();
    else setError('Failed to save — check your connection and admin access.');
  };

  // Renaming onto an existing department's exact name MERGES the two teams. The
  // only previous warning was 11px helper text under the field, which nobody
  // reads while typing, and the merge reported no counts and no way back.
  const submit = async () => {
    const trimmed = name.trim();
    const isRename = trimmed && trimmed !== dept;
    const targetCount = isRename ? departmentCounts[trimmed] : undefined;

    if (isRename && targetCount) {
      setPendingMerge({ target: trimmed, targetCount, movingCount: deptEmployees.length });
      return;
    }
    await persist(trimmed);
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60" onClick={onClose}>
      <div
        className="bg-white dark:bg-zinc-800 rounded-xl shadow-2xl p-5 w-80 border border-slate-200 dark:border-zinc-700"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${dept}`}
      >
        <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100 dark:border-zinc-700">
          <p className="text-sm font-semibold text-slate-900 dark:text-zinc-100 truncate">Edit {dept}</p>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 dark:hover:text-zinc-300"><X size={16} /></button>
        </div>

        <div className="mb-4">
          <label className="block text-[10px] font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1.5">Department name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} className={FIELD_CLASSES} />
          {/* Live warning while typing, rather than only after the fact */}
          {name.trim() !== dept && departmentCounts[name.trim()] ? (
            <p className="mt-1 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
              "{name.trim()}" already exists with {departmentCounts[name.trim()]} people — saving merges the two teams.
            </p>
          ) : (
            <p className="mt-1 text-[11px] text-slate-400 dark:text-zinc-500">
              Renaming to another department's exact name merges the two teams.
            </p>
          )}
        </div>

        <div className="mb-5">
          <label className="block text-[10px] font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1.5">Department lead</label>
          <StyledSelect
            value={leadId}
            onChange={setLeadId}
            placeholder="— Automatic (most reports) —"
            options={[
              { value: '', label: '— Automatic (most reports) —' },
              ...deptEmployees.map((e) => ({ value: e.id, label: `${e.displayName}${e.jobTitle ? ` · ${e.jobTitle}` : ''}` })),
            ]}
          />
        </div>

        {error && <p className="text-xs text-rose-600 mb-3">{error}</p>}

        <div className="flex gap-2">
          <button onClick={submit} disabled={saving || !name.trim()} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold py-2 rounded-lg transition-colors disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onClose} className="flex-1 bg-slate-100 dark:bg-zinc-700 hover:bg-slate-200 dark:hover:bg-zinc-600 text-slate-700 dark:text-zinc-300 text-sm py-2 rounded-lg transition-colors">Cancel</button>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(pendingMerge)}
        tone="warning"
        title={`Merge ${dept} into ${pendingMerge?.target}?`}
        body={`${pendingMerge?.movingCount} ${pendingMerge?.movingCount === 1 ? 'person' : 'people'} move from ${dept} into ${pendingMerge?.target}, which already has ${pendingMerge?.targetCount}. The combined department will show ${(pendingMerge?.movingCount || 0) + (pendingMerge?.targetCount || 0)}.`}
        detail={`${dept} stops existing as a department. Undoing this means renaming the people back, one correction at a time.`}
        confirmLabel="Merge departments"
        confirmPhrase={pendingMerge?.target}
        onConfirm={async () => {
          const target = pendingMerge.target;
          setPendingMerge(null);
          await persist(target);
        }}
        onCancel={() => setPendingMerge(null)}
      />
    </div>,
    document.body
  );
}

/**
 * Premium team page for a single department — hero header in the
 * department's identity, featured lead, then the team grouped by office.
 */
// Seniority bands for the Role grouping — heuristic on title keywords, same
// approach as the org chart's leadership-tier matching.
function roleBand(title = '') {
  const t = title.toLowerCase();
  if (/(principal|director|executive|chief|founder|president|officer|partner)/.test(t)) return 'Leadership';
  if (/(manager|supervisor|\blead\b|department head)/.test(t)) return 'Managers';
  return 'Team';
}
const ROLE_ORDER = ['Leadership', 'Managers', 'Team'];
const GROUP_ICONS = { role: { Leadership: Award, Managers: Briefcase, Team: Users } };

/**
 * A member row in the department team view.
 *
 * Matches the directory's row: 52px, avatar with presence, name over abbreviated
 * title, tenure chip, office, and a contact action on hover. Defined at module
 * scope — the card it replaces was declared inside DepartmentTeam, so React saw a
 * new component type on every render and remounted every row.
 */
/**
 * The roster's row grid and its header must come from one place, or the labels
 * drift off the columns they name. Leading 28px track is the selection checkbox:
 * the mockup feeds this view's selection into the same SelectionActionBar the
 * Directory uses, so a team can be exported without going back to the Directory
 * and re-finding everyone.
 */
const TEAM_ROW_GRID =
  'grid-cols-[28px_36px_minmax(0,1fr)_28px] ' +
  'md:grid-cols-[28px_36px_minmax(220px,360px)_56px_minmax(130px,220px)_minmax(0,1fr)_28px]';

function TeamRosterHeader({ sortBy, onSortChange, showOffice, allSelected, someSelected, onToggleAll }) {
  const label = (id, text, extra = '') => {
    const active = sortBy === id;
    return (
      <button
        onClick={() => onSortChange(id)}
        className={`inline-flex items-center gap-1 hover:text-ink-2 transition-colors ${extra} ${active ? 'text-accent' : ''}`}
        {...(active ? { 'aria-sort': 'ascending' } : {})}
      >
        {text}
        {active && (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        )}
      </button>
    );
  };

  return (
    <div className={`hidden md:grid w-full items-center gap-4 px-3.5 h-[34px] bg-surface-2 border-b border-line
                     ${TEAM_ROW_GRID} text-[10.5px] font-bold uppercase tracking-[0.08em] text-ink-3`}>
      <span>
        {onToggleAll && (
          <button
            onClick={onToggleAll}
            aria-label={allSelected ? 'Clear selection' : 'Select everyone in this team'}
            title={allSelected ? 'Clear selection' : 'Select everyone in this team'}
            className={`w-[15px] h-[15px] rounded-[4px] border inline-flex items-center justify-center transition-colors
              ${allSelected || someSelected
                ? 'bg-accent border-accent text-[var(--on-accent)]'
                : 'bg-surface border-line-strong hover:border-accent'}`}
          >
            {allSelected && (
              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
            {!allSelected && someSelected && <span className="w-2 h-[2px] bg-current rounded-full" />}
          </button>
        )}
      </span>
      <span />
      <span className="min-w-0">{label('lastName', 'Name')}</span>
      <span className="min-w-0">{label('tenure', 'Tenure')}</span>
      {showOffice
        ? <span className="min-w-0">{label('officeLocation', 'Office')}</span>
        : <span className="min-w-0">{label('department', 'Department')}</span>}
      <span aria-hidden="true" />
      <span />
    </div>
  );
}

function DepartmentMemberRow({ employee, index, photo, showOffice, onSelect, isSelected, onToggleSelection }) {
  const initials = (employee.displayName || '?')
    .split(/\s+/).map((part) => part[0]).join('').toUpperCase().slice(0, 2);
  const chat = teamsChatUrl(employee.mail);

  return (
    <div
      onClick={() => onSelect?.(employee)}
      className={`group grid items-center gap-4 px-3.5 h-[52px] cursor-pointer border-b border-line last:border-b-0
                 ${TEAM_ROW_GRID}
                 hover:bg-accent-soft transition-colors
                 before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[2px] before:bg-accent
                 before:opacity-0 hover:before:opacity-100 relative
                 ${isSelected ? 'bg-accent-soft before:opacity-100' : ''}`}
      style={{ animationDelay: `${Math.min(index, 12) * 12}ms` }}
    >
      <div
        onClick={(e) => { e.stopPropagation(); onToggleSelection?.(employee); }}
        className="flex items-center justify-center"
      >
        <button
          aria-label={`${isSelected ? 'Deselect' : 'Select'} ${employee.displayName}`}
          aria-pressed={isSelected}
          className={`w-[15px] h-[15px] rounded-[4px] border inline-flex items-center justify-center transition-colors
            ${isSelected
              ? 'bg-accent border-accent text-[var(--on-accent)]'
              : 'bg-surface border-line-strong opacity-0 group-hover:opacity-100 focus:opacity-100 hover:border-accent'}`}
        >
          {isSelected && (
            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
      </div>

      <div className="relative flex-none">
        {photo ? (
          <img src={photo} alt="" className="w-9 h-9 rounded-full object-cover object-top ring-1 ring-line-strong" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-surface-2 ring-1 ring-line-strong flex items-center justify-center">
            <span className="text-[11px] font-semibold text-ink-3 select-none">{initials}</span>
          </div>
        )}
      </div>

      <div className="min-w-0">
        <p className="text-[13.5px] font-semibold text-ink truncate leading-tight tracking-[-0.01em]">
          {employee.displayName}
        </p>
        <p className="text-[11.5px] text-ink-3 truncate mt-[1px] leading-snug" title={employee.jobTitle || ''}>
          {abbreviateTitle(employee.jobTitle || 'Employee')}
        </p>
      </div>

      <div className="hidden md:block">
        <TenureChip hireDate={employee.hireDate} />
      </div>

      <div className="hidden md:block min-w-0">
        {showOffice ? (
          <span className="block text-[12px] text-ink-3 truncate">{employee.officeLocation || '—'}</span>
        ) : (
          <span className="block text-[12px] text-ink-3 truncate">{employee.mail || ''}</span>
        )}
      </div>

      <div className="hidden md:block" aria-hidden="true" />

      <div className="justify-self-end" onClick={(e) => e.stopPropagation()}>
        {chat && (
          <a
            href={chat}
            target="_blank"
            rel="noopener noreferrer"
            title={`Message ${employee.displayName} in Teams`}
            aria-label={`Message ${employee.displayName} in Teams`}
            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-ink-4
                       opacity-0 group-hover:opacity-100 hover:text-accent hover:bg-surface-2 transition-all"
          >
            <MessageSquare size={13} />
          </a>
        )}
      </div>
    </div>
  );
}

export function DepartmentTeam({
  department,
  employees,
  photos,
  onSelectEmployee,
  onBack,
  isAdmin = false,
  onSettingsChange,
  departmentCounts = {},
  selectedEmployees = [],
  onToggleSelection,
  onSelectMany,
  stickyTop = 128,
}) {
  const isDark = useIsDark();
  const [editing, setEditing] = useState(false);
  const [groupBy, setGroupBy] = useState('office'); // 'office' | 'role' | 'alpha'
  // Roster sort is local to this view: the Directory's sort answers "how do I
  // scan 739 people", which is a different question from "who is in this team".
  const [sortBy, setSortBy] = useState('lastName');
  const sectionRefs = useRef({});

  const scheme   = deptScheme(department);
  const DeptIcon = getDeptIcon(department);
  const settings = getDeptSettings();

  const pinnedLeadId = settings[department]?.leadId;
  const pinnedLead = pinnedLeadId ? employees.find((e) => e.id === pinnedLeadId) : null;
  // DepartmentTeam features the lead but does not label the relationship, so it
  // does not need the confirmed/inferred distinction the index card makes.
  const lead = pinnedLead || findDeptLead(employees);
  const leadPhoto = lead ? (photos?.[lead.id] || lead.openAssetPhoto) : null;
  const online = employees.filter(isOnline).length;

  // Members grouped by the active mode, lead featured (not repeated).
  // Office: largest office first. Role: Leadership → Managers → Team.
  // A–Z: one flat alphabetical grid.
  const members = employees.filter((e) => !lead || e.id !== lead.id);

  let groups;
  if (groupBy === 'alpha') {
    groups = members.length ? [['Everyone', sortEmployees(members, sortBy)]] : [];
  } else {
    const keyOf = groupBy === 'role'
      ? (e) => roleBand(e.jobTitle)
      : (e) => e.officeLocation || 'No office listed';
    groups = Object.entries(
      members.reduce((acc, e) => {
        const key = keyOf(e);
        (acc[key] = acc[key] || []).push(e);
        return acc;
      }, {})
    )
      .sort((a, b) => groupBy === 'role'
        ? ROLE_ORDER.indexOf(a[0]) - ROLE_ORDER.indexOf(b[0])
        : b[1].length - a[1].length || a[0].localeCompare(b[0]))
      .map(([key, people]) => [key, sortEmployees(people, sortBy)]);
  }
  const officeGroups = groups; // section render below uses this name
  const selectedIds = new Set(selectedEmployees.map((e) => e.id));
  const rosterSelected = members.filter((e) => selectedIds.has(e.id)).length;
  const allSelected = members.length > 0 && rosterSelected === members.length;
  // Select-all acts on the whole team, not the group under the cursor — the
  // export is the point, and an export of one office is a different intent.
  const toggleAll = onSelectMany
    ? () => onSelectMany(allSelected ? [] : members)
    : null;
  const officeCount = new Set(employees.map((e) => e.officeLocation).filter(Boolean)).size;



  return (
    <div>
      {/* Back */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm font-medium transition-colors mb-4"
        style={{ color: isDark ? '#a1a1aa' : '#64748b' }}
        onMouseEnter={(e) => { e.currentTarget.style.color = isDark ? '#5eead4' : '#0f766e'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = isDark ? '#a1a1aa' : '#64748b'; }}
      >
        <ArrowRight size={14} className="rotate-180" />
        All Departments
      </button>

      {/* Hero */}
      <div
        className="relative rounded-2xl overflow-hidden mb-6"
        style={{
          background: isDark ? 'rgba(28,28,32,1)' : '#ffffff',
          border: `1px solid ${scheme.accent}30`,
          boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.35)' : '0 2px 10px rgba(0,0,0,0.06)',
        }}
      >
        <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, var(--accent), #2DD4BF)' }} />
        <DeptIcon
          aria-hidden="true"
          className="hidden absolute -right-6 -bottom-6 w-40 h-40 pointer-events-none"
          strokeWidth={0.75}
          style={{ color: scheme.accent, opacity: 0.07, transform: 'rotate(-8deg)' }}
        />
        {/* .dhero — mockup 3A drops this from ~150px to 76px and keeps everything
            it carried: icon, name, headcount, offices, online, lead, admin pencil.
            The roster is the point of the page; the hero was eating the space
            before the first name. */}
        <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              className="w-10 h-10 rounded-[9px] flex items-center justify-center flex-shrink-0"
              style={{
                background: isDark ? `${scheme.accent}1A` : `${scheme.accent}12`,
                border: `1px solid ${isDark ? `${scheme.accent}30` : `${scheme.accent}22`}`,
              }}
            >
              <DeptIcon className="w-5 h-5 text-ink-3" strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
              <h2 className="text-[18px] font-extrabold tracking-[-0.02em] leading-tight truncate text-ink">
                {department}
              </h2>
              <p className="text-sm mt-1" style={{ color: isDark ? '#71717a' : '#64748b' }}>
                {employees.length === 1 ? '1 person' : `${employees.length} people`}
                {officeCount > 0 && ` · ${officeCount === 1 ? '1 office' : `${officeCount} offices`}`}
                {online > 0 && (
                  <span className="inline-flex items-center gap-1 ml-2 text-xs font-medium" style={{ color: isDark ? '#86efac' : '#16a34a' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {online} online
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Featured lead */}
          {lead && (
            <button
              onClick={() => onSelectEmployee?.(lead)}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl text-left transition-all duration-150 hover:-translate-y-[1px] flex-shrink-0"
              style={{
                background: isDark ? `${scheme.accent}12` : `${scheme.accent}0A`,
                border: `1px solid ${scheme.accent}30`,
              }}
            >
              {leadPhoto ? (
                <img src={leadPhoto} alt="" className="w-9 h-9 rounded-full object-cover object-top flex-shrink-0" />
              ) : (
                <span className="w-9 h-9 rounded-full bg-surface-2 border border-line flex items-center justify-center text-[11px] font-bold text-ink-3 flex-shrink-0">
                  {initials(lead.displayName)}
                </span>
              )}
              <span className="min-w-0">
                <span className="block text-[9px] font-bold uppercase tracking-[0.16em] text-ink-4">
                  Department Lead
                </span>
                <span className="block text-sm font-semibold truncate" style={{ color: isDark ? '#f4f4f5' : '#0f172a' }}>{lead.displayName}</span>
                <span className="block text-[11px] truncate" style={{ color: isDark ? '#a1a1aa' : '#64748b' }}>{lead.jobTitle}</span>
              </span>
            </button>
          )}

          {isAdmin && (
            <button
              onClick={() => setEditing(true)}
              aria-label={`Edit ${department}`}
              title="Edit department"
              className="self-start sm:self-center w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
              style={{
                background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.05)',
                color: isDark ? '#a1a1aa' : '#475569',
              }}
            >
              <Pencil size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Controls row: jump pills (left) + group-by toggle (right). Pills
          smooth-scroll to their section in whichever grouping is active. */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          {officeGroups.length > 1 && officeGroups.map(([key, people]) => (
            <button
              key={key}
              onClick={() => sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="inline-flex items-center gap-1.5 h-8 rounded-full px-3 text-[10.5px] font-semibold whitespace-nowrap transition-colors"
              style={{
                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.88)',
                color: isDark ? '#d4d4d8' : '#0f172a',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(148,163,184,0.18)'}`,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${scheme.accent}60`; e.currentTarget.style.color = isDark ? scheme.badgeText : scheme.textLight; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(148,163,184,0.18)'; e.currentTarget.style.color = isDark ? '#d4d4d8' : '#0f172a'; }}
            >
              {key}
              <span className="text-[9px] font-bold rounded-full px-1.5 py-px" style={{ background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)', color: isDark ? '#a1a1aa' : '#64748b' }}>
                {people.length}
              </span>
            </button>
          ))}
        </div>

        {/* Group-by segmented control */}
        <div
          className="flex items-center gap-0.5 p-0.5 rounded-full flex-shrink-0"
          style={{
            background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.88)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(148,163,184,0.18)'}`,
          }}
          role="group"
          aria-label="Group team by"
        >
          {[
            { id: 'office', label: 'Office' },
            { id: 'role', label: 'Role' },
            { id: 'alpha', label: 'A–Z' },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setGroupBy(opt.id)}
              aria-pressed={groupBy === opt.id}
              className="h-7 rounded-full px-3 text-[10.5px] font-semibold whitespace-nowrap transition-colors"
              style={groupBy === opt.id
                ? { background: '#0d9488', color: '#ffffff' }
                : { color: isDark ? '#a1a1aa' : '#64748b' }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Team grouped by office */}
      <div className="space-y-7">
        {officeGroups.map(([key, people]) => {
          const SectionIcon = groupBy === 'office' ? MapPin : (GROUP_ICONS.role[key] || Users);
          return (
            <section key={key} ref={(el) => { sectionRefs.current[key] = el; }} className="scroll-mt-36">
              {/* .grouphdr — sticky, so the group you are reading is still named
                  40 rows in. Carries its count for the same reason the letter
                  bands do in the Directory. */}
              <div
                className="sticky z-10 flex items-center gap-3 mb-3 py-1.5 bg-canvas/95 backdrop-blur-[2px]"
                style={{ top: stickyTop }}
              >
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-ink-3">
                  <SectionIcon size={11} /> {key}
                </span>
                <span className="font-mono text-[11px] font-semibold text-ink-3">{people.length}</span>
                <div className="flex-1 h-px bg-line" />
              </div>
              {/* The directory row, not a bespoke card. This view used to render
                  3-up 40px-avatar cards with no tenure, no contact action, no
                  sort and no office when grouped BY office — every convention
                  from the directory abandoned for the same job. */}
              <div className="rounded-card overflow-hidden bg-surface border border-line shadow-e1">
                <TeamRosterHeader
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  showOffice={groupBy !== 'office'}
                  allSelected={allSelected}
                  someSelected={rosterSelected > 0 && !allSelected}
                  onToggleAll={toggleAll}
                />
                {people.map((employee, index) => (
                  <DepartmentMemberRow
                    key={employee.id}
                    employee={employee}
                    index={index}
                    photo={photos?.[employee.id] || employee.openAssetPhoto}
                    showOffice={groupBy !== 'office'}
                    onSelect={onSelectEmployee}
                    isSelected={selectedIds.has(employee.id)}
                    onToggleSelection={onToggleSelection}
                  />
                ))}
              </div>
            </section>
          );
        })}
        {members.length === 0 && !lead && (
          <p className="text-sm text-center py-12" style={{ color: isDark ? '#71717a' : '#94a3b8' }}>No team members found.</p>
        )}
      </div>

      {editing && (
        <DeptEditModal
          dept={department}
          deptEmployees={employees}
          departmentCounts={departmentCounts}
          currentLeadId={settings[department]?.leadId || ''}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onSettingsChange?.();
          }}
        />
      )}
    </div>
  );
}

/**
 * Pulse skeleton shown until corrections/renames have been applied to the
 * roster — prevents the flash of pre-correction department names.
 */
export function DepartmentListSkeleton({ count = 9 }) {
  const isDark = useIsDark();
  const surface = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)';
  return (
    <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(252px,1fr))]">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-2xl overflow-hidden animate-pulse"
          style={{
            background: isDark ? 'rgba(28,28,32,1)' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(148,163,184,0.14)'}`,
          }}
        >
          <div className="h-1 w-full" style={{ background: surface }} />
          <div className="px-5 pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl" style={{ background: surface }} />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 rounded w-2/3" style={{ background: surface }} />
                <div className="h-2.5 rounded w-1/3" style={{ background: surface }} />
              </div>
            </div>
            <div className="h-12 rounded-xl mt-3.5" style={{ background: surface }} />
            <div className="flex -space-x-2 mt-3.5">
              {Array.from({ length: 5 }).map((_, j) => (
                <div key={j} className="w-7 h-7 rounded-full" style={{ background: surface, boxShadow: `0 0 0 2px ${isDark ? '#1c1c20' : '#ffffff'}` }} />
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function DepartmentList({ departments, employees, photos, onSelectDepartment, isAdmin = false, onSettingsChange, onReviewUnassigned }) {
  // Needed to detect that a rename would merge two teams, and to say how many
  // people each side has.
  const departmentCounts = useMemo(() => {
    const counts = {};
    for (const e of employees) {
      if (e.department) counts[e.department] = (counts[e.department] || 0) + 1;
    }
    return counts;
  }, [employees]);
  const isDark = useIsDark();
  const [hoveredDept, setHoveredDept] = useState(null);
  const [editingDept, setEditingDept] = useState(null);
  const deptSettings = getDeptSettings();

  // People with no department at all. `departments` filters falsy values, so
  // these 50-odd people appear in NO department card and are invisible here —
  // which is a data-quality problem worth surfacing, not a team worth listing.
  const unassigned = employees.filter((e) => !e.department);

  // Share bars scale against the biggest department, so the bars compare rather
  // than just repeat the number.
  const largestDept = Math.max(
    0,
    ...departments.map((d) => employees.filter((e) => e.department === d).length),
  );

  // Largest teams first — the grid reads as an at-a-glance org footprint.
  const ordered = [...departments].sort((a, b) => {
    const countA = employees.filter((e) => e.department === a).length;
    const countB = employees.filter((e) => e.department === b).length;
    return countB - countA || a.localeCompare(b);
  });

  return (
    <>
      {/* .notice — mockup 2A pulls data quality out of the grid. A card in the grid
          still reads as a department; a band reads as a problem to fix. */}
      {unassigned.length > 0 && (
        <div className="flex items-start gap-2.5 p-3 mb-3 rounded-card border border-amber-300/70 dark:border-amber-900/60
                        bg-amber-50/70 dark:bg-amber-950/25">
          <AlertTriangle size={14} className="mt-[1px] flex-none text-amber-700 dark:text-amber-400" />
          <p className="text-[12.5px] leading-[1.5] text-ink-2 flex-1 min-w-0">
            <b className="font-bold text-ink">{unassigned.length} people have no department</b> in Azure AD, so they
            appear in the directory but in no card below. Fixing the field upstream, or recording a
            correction, files them.
          </p>
          {typeof onReviewUnassigned === 'function' && (
            <button
              onClick={() => onReviewUnassigned()}
              className="flex-none text-[11.5px] font-bold text-accent hover:underline"
            >
              Review
            </button>
          )}
        </div>
      )}

    <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(252px,1fr))]">
      {ordered.map((dept, i) => {
        const deptEmployees = employees.filter((e) => e.department === dept);
        const scheme   = deptScheme(dept);
        const DeptIcon = getDeptIcon(dept);
        const isHov    = hoveredDept === dept;

        // Pinned lead (admin override) wins over the most-reports heuristic
        const pinnedLeadId = deptSettings[dept]?.leadId;
        const pinnedLead = pinnedLeadId ? deptEmployees.find((e) => e.id === pinnedLeadId) : null;
        const lead = pinnedLead || findDeptLead(deptEmployees);
        const leadIsConfirmed = Boolean(pinnedLead);
        const offices = new Set(deptEmployees.map((e) => e.officeLocation).filter(Boolean)).size;
        const online  = deptEmployees.filter(isOnline).length;
        const leadPhoto = lead ? (photos?.[lead.id] || lead.openAssetPhoto) : null;

        return (
          <motion.button
            key={dept}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: Math.min(i * 0.02, 0.3) }}
            whileHover={{ y: -2, transition: { duration: 0.15, ease: 'easeOut' } }}
            onClick={() => onSelectDepartment(dept)}
            onMouseEnter={() => setHoveredDept(dept)}
            onMouseLeave={() => setHoveredDept(null)}
            /* No blanket padding here: the footer is a full-bleed recessed tray
               in dark mode and has to reach the card's edges, so padding lives
               on the zones below. The card already clips to its radius via
               overflow-hidden, so the tray needs no corner rounding of its own.
               (`gap-2.5` went with it — it was inert anyway, since everything
               was wrapped in a single child div.) */
            className={`relative text-left rounded-card overflow-hidden bg-surface-card border
                        flex flex-col transition-[box-shadow,border-color,transform] duration-150
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent
                        ${isHov ? 'border-accent-line shadow-e2' : 'border-line shadow-e1'}`}
          >
            {/* Mockup 2A: department identity is the 6px dot the directory already
                uses, and nothing else. The hue previously painted the border, a
                top bar, the shadow ring, a 96px watermark and the icon tile — five
                channels for one fact, which is why a grid of these read as a colour
                chart and teal stopped meaning "interactive". */}

            {/* Body plane. Its pb-1 plus the tray's pt-2.5 add up to the 14px
                that used to be the footer's mt-3.5, so the footer row keeps its
                exact position; the tray's pb-3.5 is the card's old bottom
                padding. Change one and the other two have to follow. */}
            <div className="pb-1">
              {/* .top — 32px neutral tile, name, then the sub line */}
              <div className="flex items-start gap-2.5 min-w-0 px-3.5 pt-3.5">
                <span className="w-8 h-8 rounded-[9px] bg-surface-2 border border-line flex items-center justify-center flex-none">
                  <DeptIcon className="w-4 h-4 text-ink-3" strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-[14px] font-bold tracking-[-0.01em] text-ink leading-[1.25] truncate">
                    {dept}
                  </h3>
                  <p className="text-[11.5px] text-ink-3 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="w-1.5 h-1.5 rounded-full flex-none"
                        style={{ background: scheme.accent }}
                        aria-hidden="true"
                      />
                      {deptEmployees.length === 1 ? '1 person' : `${deptEmployees.length} people`}
                    </span>
                    {offices > 0 && (
                      <span className="inline-flex items-center gap-0.5">
                        <MapPin size={9} />
                        {offices === 1 ? '1 office' : `${offices} offices`}
                      </span>
                    )}
                  </p>
                </div>
                {/* Mono headcount with a share bar against the largest department,
                    so the org footprint is readable without reading. Headcount was
                    a 2.3:1 grey string with no sense of scale. */}
                <div className="flex-none flex items-start gap-2">
                  <span className="text-right pt-0.5">
                    <span className="block font-mono text-[15px] font-bold tabular-nums text-ink leading-none">
                      {deptEmployees.length}
                    </span>
                    <span className="block w-12 h-1 rounded-[3px] bg-surface-2 overflow-hidden mt-1.5">
                      <span
                        className="block h-full rounded-[3px] bg-accent"
                        style={{ width: `${largestDept ? Math.max(6, Math.round((deptEmployees.length / largestDept) * 100)) : 0}%` }}
                      />
                    </span>
                  </span>

                  {/* Persistent and focusable. Hover-only opacity meant the control
                      did not exist for keyboard or touch. Kept in-flow so it does
                      not overlap the headcount it edits. */}
                  {isAdmin && (
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`Edit ${dept}`}
                      title="Edit department"
                      onClick={(e) => { e.stopPropagation(); setEditingDept(dept); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setEditingDept(dept); } }}
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-none cursor-pointer text-ink-3 hover:text-accent hover:bg-surface-2 transition-colors"
                    >
                      <Pencil size={12} />
                    </span>
                  )}
                </div>
              </div>

              {/* Leader spotlight */}
              {lead && (
                <div
                  className="flex items-center gap-2.5 mx-3.5 mt-3.5 px-2.5 py-2 rounded-xl"
                  style={{
                    background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.025)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(148,163,184,0.14)'}`,
                  }}
                >
                  {leadPhoto ? (
                    <img src={leadPhoto} alt="" className="w-8 h-8 rounded-full object-cover object-top flex-shrink-0" />
                  ) : (
                    <span
                      className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                      style={{ background: scheme.accent }}
                    >
                      {initials(lead.displayName)}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-[9px] font-bold uppercase tracking-[0.14em] flex items-center gap-1"
                       style={{ color: isDark ? '#71717a' : '#94a3b8' }}>
                      {leadIsConfirmed ? 'Led by' : 'Likely lead'}
                      {!leadIsConfirmed && (
                        <span
                          title="Inferred from who has the most direct reports in this department — not confirmed. An admin can pin the real lead."
                          className="inline-flex items-center justify-center w-3 h-3 rounded-full text-[8px] font-bold cursor-help"
                          style={{
                            background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)',
                            color: isDark ? '#a1a1aa' : '#64748b',
                          }}
                        >
                          ?
                        </span>
                      )}
                    </p>
                    <p className="text-xs font-semibold truncate" style={{ color: isDark ? '#e4e4e7' : '#0f172a' }}>
                      {lead.displayName}
                      <span className="font-normal ml-1.5" style={{ color: isDark ? '#71717a' : '#94a3b8' }}>
                        {lead.jobTitle}
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer: team faces + online + view affordance — the recessed
                tray (bg-surface-tray + shadow-tray) in both themes, same
                language as the person card's action row. mt-auto pins it to
                the bottom: departments with no identifiable lead render no
                lead panel, so their card is shorter than the row it stretches
                to and the tray would otherwise float mid-card.
                "View team" is absolutely positioned: even at opacity 0 an
                in-flow span reserves width, and at narrow columns that forced
                the online badge and itself to wrap — trays came out 5px taller
                on any card with someone online. Out of flow it reserves
                nothing; the online badge crossfades out on hover so the two
                never overlap in the same corner. */}
            <div className="relative flex items-center justify-between mt-auto px-3.5 pt-2.5 pb-3.5
                            bg-surface-tray shadow-tray">
              <div className="flex items-center gap-2.5">
                <AvatarStack employees={deptEmployees} photos={photos} isDark={isDark} />
                {online > 0 && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-medium whitespace-nowrap transition-opacity duration-200"
                    style={{ color: isDark ? '#86efac' : '#16a34a', opacity: isHov ? 0 : 1 }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    {online} online
                  </span>
                )}
              </div>

              <span
                className="absolute right-3.5 top-1/2 inline-flex items-center gap-1 text-[11px] font-semibold whitespace-nowrap transition-all duration-200"
                style={{
                  color: isDark ? scheme.badgeText : scheme.textLight,
                  opacity: isHov ? 1 : 0,
                  transform: isHov ? 'translate(0, -50%)' : 'translate(-4px, -50%)',
                }}
              >
                View team <ArrowRight size={12} />
              </span>
            </div>
          </motion.button>
        );
      })}


      {editingDept && (
        <DeptEditModal
          dept={editingDept}
          deptEmployees={employees.filter((e) => e.department === editingDept)}
          departmentCounts={departmentCounts}
          currentLeadId={deptSettings[editingDept]?.leadId || ''}
          onClose={() => setEditingDept(null)}
          onSaved={() => {
            setEditingDept(null);
            onSettingsChange?.();
          }}
        />
      )}
    </div>
    </>
  );
}
